/**
 * NotificationsContext — core state manager for AB3 notifications.
 *
 * useReducer with explicit actions, optimistic updates,
 * race-safe upsert, side-effect dispatch chain.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';

import notificationService from '../services/notification.service';
import notificationWsService, { type NotificationWsEvent } from '../services/notificationWebSocket.service';
import { useNotificationPreferences } from './NotificationPreferencesContext';
import { useAuth } from './AuthContext';
import { useAppFeatures } from './AppFeaturesContext';
import { resolveNotificationUrl } from '../utils/notification-routes';
import { isGranted } from '../utils/webNotifications';
import { pushInApp } from '../utils/inAppPush';
import { play as playSound } from '../utils/notificationSound';
import { showToast } from '../utils/toast';
import { safeGetJson, safeSetJson } from '../utils/safeStorage';

import type { NotificationOut, NotificationSeverity } from '../types/notification.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const LAST_RECEIVED_AT_KEY = 'ab3.notif.lastReceivedAt.v1';
const MAX_ITEMS = 200;
const PERSIST_THROTTLE_MS = 30_000;
const INITIAL_LOAD_LIMIT = 20;
const LOAD_MORE_LIMIT = 20;

// ─── State / Actions ─────────────────────────────────────────────────────────

export interface NotificationsState {
  items: Map<number, NotificationOut>;
  total: number;
  unreadCount: number;
  lastReceivedAt: string | null;
  connected: boolean;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
}

export type Action =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; items: NotificationOut[]; total: number; replaceAll: boolean }
  | { type: 'LOAD_FAILURE'; error: string }
  | { type: 'UPSERT'; payload: NotificationOut }
  | { type: 'DELETE'; id: number }
  | { type: 'MARK_READ_OPTIMISTIC'; id: number }
  | { type: 'MARK_READ_ROLLBACK'; id: number; snapshot: NotificationOut }
  | { type: 'DISMISS_OPTIMISTIC'; id: number }
  | { type: 'DISMISS_ROLLBACK'; id: number; snapshot: NotificationOut }
  | { type: 'SET_CONNECTED'; connected: boolean }
  | { type: 'SET_LAST_RECEIVED_AT'; ts: string }
  | { type: 'RESET' };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function recomputeUnreadCount(items: Map<number, NotificationOut>): number {
  let count = 0;
  items.forEach((n) => {
    if (!n.is_read && !n.is_dismissed) count++;
  });
  return count;
}

function maxISO(a: string | null, b: string): string {
  if (!a) return b;
  return a > b ? a : b;
}

/**
 * Evict oldest items by created_at, keeping newest MAX_ITEMS.
 */
function capItems(items: Map<number, NotificationOut>): Map<number, NotificationOut> {
  if (items.size <= MAX_ITEMS) return items;
  const sorted = [...items.values()].sort((a, b) => {
    if (a.created_at < b.created_at) return -1;
    if (a.created_at > b.created_at) return 1;
    return 0;
  });
  // Remove oldest until size == MAX_ITEMS
  const toRemove = sorted.slice(0, items.size - MAX_ITEMS);
  const newItems = new Map(items);
  toRemove.forEach((n) => newItems.delete(n.id));
  return newItems;
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

export const initialState: NotificationsState = {
  items: new Map(),
  total: 0,
  unreadCount: 0,
  lastReceivedAt: safeGetJson<string | null>(LAST_RECEIVED_AT_KEY, null),
  connected: false,
  loading: false,
  error: null,
  hasMore: false,
};

/** Alias for testability — the spec requires this name. */
export const initialNotificationsState: NotificationsState = initialState;

export function notificationsReducer(
  state: NotificationsState,
  action: Action
): NotificationsState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };

    case 'LOAD_SUCCESS': {
      let newItems: Map<number, NotificationOut>;
      if (action.replaceAll) {
        newItems = new Map(action.items.map((n) => [n.id, n]));
      } else {
        newItems = new Map(state.items);
        action.items.forEach((n) => {
          const existing = newItems.get(n.id);
          if (!existing || n.updated_at >= existing.updated_at) {
            newItems.set(n.id, n);
          }
        });
      }
      newItems = capItems(newItems);
      return {
        ...state,
        loading: false,
        error: null,
        items: newItems,
        total: action.total,
        unreadCount: recomputeUnreadCount(newItems),
        hasMore: action.total > newItems.size,
      };
    }

    case 'LOAD_FAILURE':
      return { ...state, loading: false, error: action.error };

    case 'UPSERT': {
      const { payload } = action;
      const existing = state.items.get(payload.id);
      // Race-safe: ignore stale updates (tie → existing wins)
      if (existing && payload.updated_at <= existing.updated_at) {
        return state;
      }
      let newItems = new Map(state.items);
      newItems.set(payload.id, payload);
      newItems = capItems(newItems);
      const newLastReceivedAt =
        existing === undefined
          ? maxISO(state.lastReceivedAt, payload.created_at)
          : state.lastReceivedAt;
      return {
        ...state,
        items: newItems,
        unreadCount: recomputeUnreadCount(newItems),
        lastReceivedAt: newLastReceivedAt,
      };
    }

    case 'DELETE': {
      const newItems = new Map(state.items);
      newItems.delete(action.id);
      return {
        ...state,
        items: newItems,
        unreadCount: recomputeUnreadCount(newItems),
      };
    }

    case 'MARK_READ_OPTIMISTIC': {
      const item = state.items.get(action.id);
      if (!item) return state;
      const newItems = new Map(state.items);
      newItems.set(action.id, {
        ...item,
        is_read: true,
        read_at: new Date().toISOString(),
      });
      return {
        ...state,
        items: newItems,
        unreadCount: recomputeUnreadCount(newItems),
      };
    }

    case 'MARK_READ_ROLLBACK': {
      const newItems = new Map(state.items);
      newItems.set(action.id, action.snapshot);
      return {
        ...state,
        items: newItems,
        unreadCount: recomputeUnreadCount(newItems),
      };
    }

    case 'DISMISS_OPTIMISTIC': {
      const item = state.items.get(action.id);
      if (!item) return state;
      const newItems = new Map(state.items);
      newItems.set(action.id, {
        ...item,
        is_dismissed: true,
        dismissed_at: new Date().toISOString(),
      });
      return {
        ...state,
        items: newItems,
        unreadCount: recomputeUnreadCount(newItems),
      };
    }

    case 'DISMISS_ROLLBACK': {
      const newItems = new Map(state.items);
      newItems.set(action.id, action.snapshot);
      return {
        ...state,
        items: newItems,
        unreadCount: recomputeUnreadCount(newItems),
      };
    }

    case 'SET_CONNECTED':
      return { ...state, connected: action.connected };

    case 'SET_LAST_RECEIVED_AT':
      return { ...state, lastReceivedAt: action.ts };

    case 'RESET':
      return { ...initialState, lastReceivedAt: state.lastReceivedAt };

    default:
      return state;
  }
}

// ─── Toast rate-limiter / coalescer ──────────────────────────────────────────

const TOAST_WINDOW_MS = 3000;
const TOAST_THRESHOLD = 3;

class ToastCoalescer {
  private timestamps: number[] = [];
  private timerActive = false;
  private pendingCount = 0;
  private pendingSeverities: NotificationSeverity[] = [];

  /** Reset all internal state — used in tests to prevent cross-test pollution. */
  reset(): void {
    this.timestamps = [];
    this.timerActive = false;
    this.pendingCount = 0;
    this.pendingSeverities = [];
  }

  dispatch(message: string, severity: NotificationSeverity): void {
    const now = Date.now();
    this.timestamps = this.timestamps.filter((t) => now - t < TOAST_WINDOW_MS);
    this.timestamps.push(now);

    if (this.timerActive) {
      // During coalesce window — just count
      this.pendingCount++;
      this.pendingSeverities.push(severity);
      return;
    }

    if (this.timestamps.length < TOAST_THRESHOLD) {
      // Under threshold — fire immediately
      showToast(message, severity === 'error' ? 'error' : severity === 'warning' ? 'warning' : 'success', 4500);
      return;
    }

    // At or over threshold — fire coalesced
    const count = this.timestamps.length;
    this.pendingCount = 0;
    this.pendingSeverities = [severity];
    this.timerActive = true;
    this._fireCoalesced(count, [severity]);

    setTimeout(() => {
      this.timerActive = false;
      if (this.pendingCount > 0) {
        this._fireCoalesced(this.pendingCount, this.pendingSeverities);
        this.pendingCount = 0;
        this.pendingSeverities = [];
      }
    }, TOAST_WINDOW_MS);
  }

  private _fireCoalesced(count: number, severities: NotificationSeverity[]): void {
    const type = severities.includes('error')
      ? 'error'
      : severities.includes('warning')
        ? 'warning'
        : 'success';
    showToast(`${count} новых уведомлений`, type, 4500);
  }
}

const toastCoalescer = new ToastCoalescer();

/** Exposed for testing only — resets singleton state to prevent cross-test pollution. */
export const _resetToastCoalescerForTesting = () => toastCoalescer.reset();

// ─── Context shape ────────────────────────────────────────────────────────────

interface NotificationsContextValue {
  state: NotificationsState;
  dispatch: React.Dispatch<Action>;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

// ─── Actions context (separate to avoid re-render on state change) ────────────

interface NotificationsActionsValue {
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: (source_service?: string) => Promise<void>;
  dismiss: (id: number) => Promise<void>;
  markManyRead: (ids: number[]) => Promise<void>;
  dismissMany: (ids: number[]) => Promise<void>;
}

const NotificationsActionsContext = createContext<NotificationsActionsValue | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationsReducer, initialState);

  const { prefs, isDND } = useNotificationPreferences();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const features = useAppFeatures();
  const notificationsEnabled = features.notifications === true;
  const navigate = useNavigate();

  // Stable refs so async callbacks always read latest
  const stateRef = useRef(state);
  stateRef.current = state;
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const isDNDRef = useRef(isDND);
  isDNDRef.current = isDND;
  const notificationsEnabledRef = useRef(notificationsEnabled);
  notificationsEnabledRef.current = notificationsEnabled;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Throttle timer for persisting lastReceivedAt
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Polling fallback refs ─────────────────────────────────────────────────
  // Internal to Provider — no need for dispatch, no re-renders required.
  const pollingActiveRef = useRef(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Persist lastReceivedAt throttled ──────────────────────────────────────
  useEffect(() => {
    if (state.lastReceivedAt === null) return;
    if (persistTimerRef.current !== null) return; // throttle — already scheduled
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null;
      safeSetJson(LAST_RECEIVED_AT_KEY, stateRef.current.lastReceivedAt);
    }, PERSIST_THROTTLE_MS);
  }, [state.lastReceivedAt]);

  // ── Side-effects helper ───────────────────────────────────────────────────
  // useCallback deps empty by design: this function reads `prefsRef.current` and
  // `isDNDRef.current()` via refs to stay stable across preference changes,
  // preventing re-subscription of WS handlers.
  const dispatchSideEffects = useCallback((n: NotificationOut) => {
    const severity = n.severity;
    const currentPrefs = prefsRef.current;
    const dndActive = isDNDRef.current();

    function shouldFire(channel: 'sound' | 'os'): boolean {
      if (dndActive) return false;
      const pref = currentPrefs[channel];
      if (pref === 'off') return false;
      if (pref === 'warn_err' && severity === 'info') return false;
      return true;
    }

    if (shouldFire('sound')) {
      playSound({ suppressWhenHiddenAndOsGranted: isGranted() });
    }

    if (shouldFire('os')) {
      const url = resolveNotificationUrl(n.entity_type, n.entity_id) ?? '/notifications';
      pushInApp({
        title: n.title,
        body: n.body ?? null,
        severity,
        onClick: () => {
          const snapshot = stateRef.current.items.get(n.id);
          if (snapshot && !snapshot.is_read) {
            dispatch({ type: 'MARK_READ_OPTIMISTIC', id: n.id });
            notificationService.markRead(n.id).catch((err) => {
              dispatch({ type: 'MARK_READ_ROLLBACK', id: n.id, snapshot });
              console.warn('[NotificationsContext] markRead from push failed', err);
            });
          }
          navigateRef.current(url);
        },
      });
    }
  }, []);

  // ── WS message handler ────────────────────────────────────────────────────
  const handleWsMessage = useCallback(
    async (ev: NotificationWsEvent) => {
      if (!notificationsEnabledRef.current) return;
      if (ev.op === 'DELETE') {
        dispatch({ type: 'DELETE', id: ev.id });
        return;
      }
      // Backend may send full payload inline (`data`); fall back to REST getById otherwise.
      try {
        const payload = ev.payload ?? (await notificationService.getById(ev.id));
        dispatch({ type: 'UPSERT', payload });
        if (ev.op === 'INSERT') {
          dispatchSideEffects(payload);
        }
      } catch (err) {
        console.warn('[NotificationsContext] Failed to fetch notification by id', ev.id, err);
      }
    },
    [dispatchSideEffects]
  );

  // ── Initial mount: load list + connect WS ─────────────────────────────────
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      dispatch({ type: 'RESET' });
      return;
    }
    // Feature flag gate: если notifications выключены — ни API, ни WS
    if (!notificationsEnabled) {
      dispatch({ type: 'RESET' });
      notificationWsService.disconnect();
      return;
    }

    let cancelled = false;

    async function initialLoad() {
      dispatch({ type: 'LOAD_START' });
      try {
        const { items, total } = await notificationService.list({ limit: INITIAL_LOAD_LIMIT });
        if (!cancelled) {
          dispatch({ type: 'LOAD_SUCCESS', items, total, replaceAll: true });
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Ошибка загрузки уведомлений';
          dispatch({ type: 'LOAD_FAILURE', error: msg });
        }
      }
    }

    initialLoad();
    notificationWsService.connect();

    return () => {
      cancelled = true;
      notificationWsService.disconnect();
      if (persistTimerRef.current !== null) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
        // Flush on unmount
        safeSetJson(LAST_RECEIVED_AT_KEY, stateRef.current.lastReceivedAt);
      }
    };
  }, [isAuthenticated, authLoading, notificationsEnabled]);

  // ── WS subscriptions ──────────────────────────────────────────────────────
  useEffect(() => {
    const unsubMsg = notificationWsService.onMessage(handleWsMessage);

    const unsubConn = notificationWsService.onConnectionChange((connected) => {
      dispatch({ type: 'SET_CONNECTED', connected });
      // If WS reconnects while polling fallback is active — stop polling.
      if (connected && pollingActiveRef.current) {
        if (pollingIntervalRef.current !== null) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        pollingActiveRef.current = false;
        showToast('Соединение восстановлено', 'success');
      }
    });

    const unsubPolling = notificationWsService.onPollingFallback(() => {
      // Design doc premise #8: if WS fails after 3 attempts → polling every 60s.
      if (!notificationsEnabledRef.current) return; // feature flag — не запускаем polling
      if (pollingActiveRef.current) return; // already polling
      pollingActiveRef.current = true;
      showToast('Соединение с уведомлениями потеряно. Использую periodic polling', 'warning');
      pollingIntervalRef.current = setInterval(() => {
        if (!notificationsEnabledRef.current) return;
        // Inline refresh to avoid capturing `actions` in closure (actions is
        // stable via useMemo([]) but we keep this self-contained for clarity).
        dispatch({ type: 'LOAD_START' });
        notificationService
          .list({ limit: INITIAL_LOAD_LIMIT })
          .then(({ items, total }) => {
            dispatch({ type: 'LOAD_SUCCESS', items, total, replaceAll: true });
          })
          .catch((err) => {
            const msg = err instanceof Error ? err.message : 'Ошибка обновления уведомлений';
            dispatch({ type: 'LOAD_FAILURE', error: msg });
          });
      }, 60_000);
    });

    return () => {
      unsubMsg();
      unsubConn();
      unsubPolling();
      // Clear polling interval on unmount.
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollingActiveRef.current = false;
    };
  }, [handleWsMessage]);

  // ── Actions ───────────────────────────────────────────────────────────────
  // useMemo deps empty by design: all mutable state is accessed via `stateRef.current`
  // and `dispatch` (stable from useReducer), keeping the actions object identity
  // stable and preventing unnecessary re-renders of consumers.
  const actions = useMemo<NotificationsActionsValue>(
    () => ({
      async refresh() {
        if (!notificationsEnabledRef.current) return;
        dispatch({ type: 'LOAD_START' });
        try {
          const { items, total } = await notificationService.list({ limit: INITIAL_LOAD_LIMIT });
          dispatch({ type: 'LOAD_SUCCESS', items, total, replaceAll: true });
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Ошибка обновления уведомлений';
          dispatch({ type: 'LOAD_FAILURE', error: msg });
        }
      },

      async loadMore() {
        if (!notificationsEnabledRef.current) return;
        const current = stateRef.current;
        if (current.loading || !current.hasMore) return;
        dispatch({ type: 'LOAD_START' });
        try {
          const { items, total } = await notificationService.list({
            limit: LOAD_MORE_LIMIT,
            skip: current.items.size,
          });
          dispatch({ type: 'LOAD_SUCCESS', items, total, replaceAll: false });
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Ошибка загрузки уведомлений';
          dispatch({ type: 'LOAD_FAILURE', error: msg });
        }
      },

      async markRead(id: number) {
        if (!notificationsEnabledRef.current) return;
        const snapshot = stateRef.current.items.get(id);
        if (!snapshot) return;
        dispatch({ type: 'MARK_READ_OPTIMISTIC', id });
        try {
          await notificationService.markRead(id);
        } catch (err) {
          dispatch({ type: 'MARK_READ_ROLLBACK', id, snapshot });
          showToast('Не удалось отметить уведомление как прочитанное', 'error');
          throw err;
        }
      },

      async markAllRead(source_service?: string) {
        if (!notificationsEnabledRef.current) return;
        const snapshots = [...stateRef.current.items.values()].filter(
          (n) => !n.is_read && (source_service === undefined || n.source_service === source_service)
        );
        snapshots.forEach((n) => dispatch({ type: 'MARK_READ_OPTIMISTIC', id: n.id }));
        try {
          await notificationService.markAllRead(source_service);
          const { items, total } = await notificationService.list({ limit: INITIAL_LOAD_LIMIT });
          dispatch({ type: 'LOAD_SUCCESS', items, total, replaceAll: true });
        } catch (err) {
          snapshots.forEach((s) =>
            dispatch({ type: 'MARK_READ_ROLLBACK', id: s.id, snapshot: s })
          );
          showToast('Не удалось отметить все как прочитанные', 'error');
        }
      },

      async dismiss(id: number) {
        if (!notificationsEnabledRef.current) return;
        const snapshot = stateRef.current.items.get(id);
        if (!snapshot) return;
        dispatch({ type: 'DISMISS_OPTIMISTIC', id });
        try {
          await notificationService.dismiss(id);
        } catch (err) {
          dispatch({ type: 'DISMISS_ROLLBACK', id, snapshot });
          showToast('Не удалось скрыть уведомление', 'error');
          throw err;
        }
      },

      async markManyRead(ids: number[]) {
        if (!notificationsEnabledRef.current) return;
        const snapshots = ids
          .map((id) => stateRef.current.items.get(id))
          .filter((n): n is NotificationOut => n !== undefined);
        snapshots.forEach((n) => dispatch({ type: 'MARK_READ_OPTIMISTIC', id: n.id }));
        try {
          await notificationService.markManyRead(ids);
        } catch (err) {
          snapshots.forEach((s) =>
            dispatch({ type: 'MARK_READ_ROLLBACK', id: s.id, snapshot: s })
          );
          showToast('Не удалось отметить уведомления как прочитанные', 'error');
        }
      },

      async dismissMany(ids: number[]) {
        if (!notificationsEnabledRef.current) return;
        const snapshots = ids
          .map((id) => stateRef.current.items.get(id))
          .filter((n): n is NotificationOut => n !== undefined);
        snapshots.forEach((n) => dispatch({ type: 'DISMISS_OPTIMISTIC', id: n.id }));
        try {
          await notificationService.dismissMany(ids);
        } catch (err) {
          snapshots.forEach((s) =>
            dispatch({ type: 'DISMISS_ROLLBACK', id: s.id, snapshot: s })
          );
          showToast('Не удалось скрыть уведомления', 'error');
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const ctxValue = useMemo<NotificationsContextValue>(
    () => ({ state, dispatch }),
    [state]
  );

  return (
    <NotificationsContext.Provider value={ctxValue}>
      <NotificationsActionsContext.Provider value={actions}>
        {children}
      </NotificationsActionsContext.Provider>
    </NotificationsContext.Provider>
  );
};

// ─── Selector hooks ──────────────────────────────────────────────────────────

export function useNotificationsUnreadCount(): number {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsUnreadCount must be used within NotificationsProvider');
  return ctx.state.unreadCount;
}

export function useNotificationsItems(tab: 'unread' | 'all'): NotificationOut[] {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsItems must be used within NotificationsProvider');
  return useMemo(() => {
    const all = [...ctx.state.items.values()].sort((a, b) =>
      a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
    );
    if (tab === 'unread') return all.filter((n) => !n.is_read && !n.is_dismissed);
    return all.filter((n) => !n.is_dismissed);
  }, [ctx.state.items, tab]);
}

export function useNotificationsConnection(): { connected: boolean; error: string | null } {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsConnection must be used within NotificationsProvider');
  return useMemo(
    () => ({ connected: ctx.state.connected, error: ctx.state.error }),
    [ctx.state.connected, ctx.state.error]
  );
}

export function useNotificationsLoading(): boolean {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsLoading must be used within NotificationsProvider');
  return ctx.state.loading;
}

export function useNotificationsActions(): NotificationsActionsValue {
  const ctx = useContext(NotificationsActionsContext);
  if (!ctx) throw new Error('useNotificationsActions must be used within NotificationsProvider');
  return ctx;
}
